import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { TokenGuard } from "../App";
import { StandardURL } from "../App";


const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  :root {
    --bg-base: #0f172a;
    --surface: #1e293b;
    --surface-hover: #334155;
    --border: #334155;
    --border-focus: #6366f1;
    --primary: #6366f1;
    --primary-hover: #4f46e5;
    --primary-dim: rgba(99, 102, 241, 0.15);
    --text-main: #f8fafc;
    --text-muted: #94a3b8;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-page {
    min-height: 100vh;
    background: var(--bg-base);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', sans-serif;
    padding: 2rem 1rem;
    position: relative;
    overflow: hidden;
  }

  .auth-page::before {
    content: '';
    position: absolute;
    top: -10%;
    left: 50%;
    transform: translateX(-50%);
    width: 80vw;
    height: 80vh;
    background: radial-gradient(circle, var(--primary-dim) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 3rem 2.5rem;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  }

  .auth-logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  .auth-logo-icon { width: 28px; height: 28px; color: var(--primary); }
  .auth-logo-text { font-size: 1.5rem; font-weight: 700; letter-spacing: -0.03em; color: var(--text-main); }

  .auth-header { text-align: center; margin-bottom: 2.5rem; }
  .auth-title { font-size: 1.75rem; font-weight: 600; color: var(--text-main); margin-bottom: 0.5rem; letter-spacing: -0.02em; }
  .auth-subtitle { color: var(--text-muted); font-size: 0.95rem; line-height: 1.5; }

  /* OTP boxes */
  .otp-row {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
  .otp-input {
    width: 52px;
    height: 60px;
    text-align: center;
    background: var(--bg-base);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-main);
    font-family: 'Inter', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    outline: none;
    transition: all 0.2s;
    caret-color: var(--primary);
  }
  .otp-input:focus {
    border-color: var(--border-focus);
    box-shadow: 0 0 0 4px var(--primary-dim);
  }
  .otp-input:not(:placeholder-shown) {
    border-color: var(--primary);
  }

  .otp-actions {
    display: flex;
    gap: 1rem;
  }

  .auth-btn {
    flex: 1;
    background: var(--primary);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0.85rem 1rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
  }
  .auth-btn:hover:not(:disabled) { background: var(--primary-hover); transform: translateY(-1px); box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35); }
  .auth-btn:disabled { background: var(--surface-hover); color: var(--text-muted); cursor: not-allowed; box-shadow: none; transform: none; }

  .auth-btn.secondary {
    background: transparent;
    color: var(--text-main);
    border: 1px solid var(--border);
    box-shadow: none;
  }
  .auth-btn.secondary:hover:not(:disabled) {
    border-color: var(--text-muted);
    background: rgba(255,255,255,0.02);
  }

  .auth-footer-text { text-align: center; margin-top: 2rem; font-size: 0.9rem; color: var(--text-muted); }
  .auth-footer-text a { color: var(--text-main); text-decoration: none; font-weight: 500; transition: color 0.2s; }
  .auth-footer-text a:hover { color: var(--primary); }
`;

const OtpVerification = () => {
  const navigate = useNavigate();
  const [otpValues, setOtpValues] = useState(["", "", "", "", ""]);
  const [formData, setFormData] = useState({ code: "" });
  const refs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (index, value) => {
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    const otpCode = newOtpValues.join("");
    setFormData({ ...formData, code: otpCode });
    if (value && index < 4) {
      refs.current[index + 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${StandardURL}/auth/otp`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.response.token) {
        localStorage.setItem("token", response.data.response.token);
        localStorage.setItem("user", JSON.stringify(response.data.response.user));
      }

      toast.success("OTP verification successful!");
      setIsLoading(false);

      const userRole = response.data.response.user?.role;
      if (userRole === "ADMIN") {
        navigate("../sms/admin");
      } else {
        navigate("../sms/start");
      }

    } catch (error) {
      console.error(error);
      setIsLoading(false);
      toast.error("An error occurred, please try again later");
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
         `${StandardURL}/auth/resend-otp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("OTP resent successfully!");
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      toast.error("An error occurred while resending OTP");
    }
  };

  return (
    <TokenGuard>
      <style>{styles}</style>
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-container">
            <svg className="auth-logo-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span className="auth-logo-text">Multiphasic Infraconstruct</span>
          </div>

          <div className="auth-header">
            <h2 className="auth-title">Verify Your Identity</h2>
            <p className="auth-subtitle">We sent a 5-digit code to your email.<br />Enter it below to continue.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="otp-row">
              {otpValues.map((value, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  className="otp-input"
                  placeholder="·"
                  ref={(input) => (refs.current[index] = input)}
                />
              ))}
            </div>

            <div className="otp-actions">
              <button type="submit" className="auth-btn" disabled={isLoading}>
                {isLoading ? (
                  <Oval height={20} width={20} color="#ffffff" visible={true} ariaLabel="loading-indicator" secondaryColor="rgba(255,255,255,0.3)" strokeWidth={4} strokeWidthSecondary={4} />
                ) : (
                  "Verify OTP"
                )}
              </button>
              <button
                type="button"
                className="auth-btn secondary"
                onClick={handleResendOtp}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Oval height={20} width={20} color="#f8fafc" visible={true} ariaLabel="loading-indicator" secondaryColor="rgba(255,255,255,0.3)" strokeWidth={4} strokeWidthSecondary={4} />
                ) : (
                  "Resend"
                )}
              </button>
            </div>
          </form>

          <p className="auth-footer-text">
            Wrong account?{" "}
            <a href="/">Back to Login</a>
          </p>
        </div>
      </div>
    </TokenGuard>
  );
};

export default OtpVerification;